import { useEffect, useRef, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '@/store/store';
import {
  fetchNotifications,
  markAllAsReadSync,
} from '@/store/slices/notificationsSlice';

import Notification from '@/components/notification';
import NotificationSkeleton from './notificationSkeleton';

const NotificationList = () => {
  const { list, loading, currentPage, total } = useSelector(
    (state: RootState) => state.notifications
  );
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);

  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    dispatch(fetchNotifications(1));
  }, [dispatch]);

  const pageSize = 10;
  const totalPages = Math.ceil(total / pageSize);

  const handleScroll = useCallback(() => {
    if (loading) return;
    const container = containerRef.current;
    if (!container) return;

    const { scrollTop, clientHeight, scrollHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      if (currentPage < totalPages) {
        dispatch(fetchNotifications(currentPage + 1));
      }
    }
  }, [dispatch, loading, currentPage, totalPages]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (loading && list.length === 0) {
      setShowSkeleton(true);
    }

    if (!loading && showSkeleton) {
      const timer = setTimeout(() => {
        setShowSkeleton(false);
      }, 1);

      return () => clearTimeout(timer);
    }
  }, [loading, list.length, showSkeleton]);

  useEffect(() => {
    if (!loading && list.length > 0 && list.some((n) => !n.is_read)) {
      const timer = setTimeout(() => {
        dispatch(markAllAsReadSync());

        if (window.electron) {
          window.electron.send('notifications:markRead');
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [loading, list, dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading) {
        dispatch(fetchNotifications(1));
      }
    }, 20000);

    return () => clearInterval(interval);
  }, [dispatch, loading]);

  if (list.length === 0 && !loading && !showSkeleton) {
    return (
      <p className="font-jostRegular text-center text-[#1F2B40]">
        Der er ingen nye meddelelser
      </p>
    );
  }

  return (
    <div>
      <div
        ref={containerRef}
        className="h-full max-h-[500px] scroll overflow-y-auto">
        {showSkeleton ? (
          <NotificationSkeleton count={5} />
        ) : (
          list.map(({ id, title, message, url, is_read, created_at }) => (
            <Notification
              key={id}
              id={id}
              title={title}
              message={message}
              url={url}
              is_read={is_read}
              created_at={created_at}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationList;
